import { useState, useEffect, useRef } from 'react';
import { LicenseScreen } from './views/LicenseScreen';
import { DashboardLayout } from './layouts/DashboardLayout';
import { seedDatabase } from './db/seed';
import { db as dexieDb } from './db/db';
import { useStore } from './store/useStore';
import ErrorBoundary from './ui/ErrorBoundary.jsx';
import { startAutoBackup, stopAutoBackup } from './services/autoBackupService';
import { DataProviderWrapper } from './services/DataProviderContext';
import { syncFromServer } from './services/clientSync';
import {
  resolveLicenseOnBoot,
  getStoredLicense,
  saveStoredLicense,
  clearStoredLicense,
  isLicenseValid,
  activateDemo,
  watchLicenseRevocation,
  checkOnline,
  LicenseInfo,
  PlanType,
  getInstallId,
} from './lib/license';

// ── Global Exit Confirmation Modal ──────────────────────────────────────────
function ExitConfirmModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); e.stopImmediatePropagation(); onConfirm(); }
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onCancel(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onConfirm, onCancel]);

  return (
    <div className="exit-confirm-modal" style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'white', width: 320,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        borderRadius: 2, overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{ background: '#1e8585', color: 'white', padding: '10px 16px', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
          Aarthika Pro
        </div>
        <div style={{ padding: '24px 24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 }}>
            Are you sure you want to exit?
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 24 }}>
            Aarthika Pro will be closed.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button autoFocus onClick={onConfirm} style={{
              background: '#1e8585', color: 'white',
              border: 'none', padding: '7px 24px',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              borderRadius: 2,
            }}>
              ✓ Yes, Exit
            </button>
            <button onClick={onCancel} style={{
              background: '#f3f4f6', color: '#374151',
              border: '1px solid #d1d5db', padding: '7px 24px',
              fontSize: 13, cursor: 'pointer', borderRadius: 2,
            }}>
              ✕ No
            </button>
          </div>
        </div>
        <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '6px', textAlign: 'center', fontSize: 10, color: '#9ca3af' }}>
          Enter = Exit &nbsp;|&nbsp; Esc = Cancel
        </div>
      </div>
    </div>
  );
}

// ── License Expiry Warning Modal (shown once on boot) ───────────────────────
function ExpiryWarningModal({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); onDismiss(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onDismiss]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'white', width: 340,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        borderRadius: 2, overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{ background: '#b45309', color: 'white', padding: '10px 16px', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
          ⚠ License Expiry Notice
        </div>
        <div style={{ padding: '24px 24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 24, lineHeight: 1.5 }}>
            {message}
          </div>
          <button autoFocus onClick={onDismiss} style={{
            background: '#1e8585', color: 'white',
            border: 'none', padding: '7px 24px',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            borderRadius: 2,
          }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

type BootState = 'checking' | 'offline-blocked' | 'ready';

// ── Trial Limit Modal ────────────────────────────────────────────────────────
function TrialLimitModal({ reason, onUpgrade, onDismiss }: {
  reason: string;
  onUpgrade: () => void;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onDismiss(); }
      if (e.key === 'Enter')  { e.preventDefault(); onUpgrade(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onDismiss, onUpgrade]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'white', width: 360,
        boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        borderRadius: 2, overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{ background: '#d97706', color: 'white', padding: '10px 16px', fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
          ⚠ Trial Limit Reached
        </div>
        <div style={{ padding: '24px 24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 10, lineHeight: 1.5 }}>
            {reason}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 24 }}>
            Upgrade to a full license to remove this limit.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button autoFocus onClick={onUpgrade} style={{
              background: '#1e8585', color: 'white',
              border: 'none', padding: '8px 20px',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              borderRadius: 2,
            }}>
              🔑 Activate License
            </button>
            <button onClick={onDismiss} style={{
              background: '#f3f4f6', color: '#374151',
              border: '1px solid #d1d5db', padding: '8px 16px',
              fontSize: 13, cursor: 'pointer', borderRadius: 2,
            }}>
              Later
            </button>
          </div>
        </div>
        <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '6px', textAlign: 'center', fontSize: 10, color: '#9ca3af' }}>
          Enter = Activate &nbsp;|&nbsp; Esc = Dismiss
        </div>
      </div>
    </div>
  );
}

function isMultiUserClient(): boolean {
  const port = window.location.port;
  return port === '7743' || window.location.search.includes('client=true');
}

export default function App() {
  const [bootState, setBootState]       = useState<BootState>(isMultiUserClient() ? 'ready' : 'checking');
  const [license, setLicense]           = useState<LicenseInfo | null>(
    isMultiUserClient() ? { type: 'full' as const, plan: 'pro' as PlanType, expiresAt: '2099-12-31', activatedAt: '', key: 'multi-user-client', deviceId: 'client' } : null
  );
  const setActiveCompany                = useStore((s) => s.setActiveCompany);
  const activeCompany                   = useStore((s) => s.activeCompany);
  const setLicenseInfo                  = useStore((s) => s.setLicenseInfo);
  const [expiryWarning, setExpiryWarning] = useState<string | null>(null);
  const expiryWarningShown = useRef(false);
  const [expirationMsg, setExpirationMsg] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [trialLimitReason, setTrialLimitReason] = useState<string | null>(null);
  const setShowLicensePanel             = useStore((s) => s.setShowLicensePanel);
  const unsubRevoke = useRef<(() => void) | null>(null);

  // Listen for trial limit events from db.js
  useEffect(() => {
    const handler = (e: Event) => {
      const reason = (e as CustomEvent).detail?.reason || 'Trial limit reached.';
      setTrialLimitReason(reason);
    };
    window.addEventListener('aarthika:trial-limit', handler);
    return () => window.removeEventListener('aarthika:trial-limit', handler);
  }, []);

  // ── Auto-backup: start/stop timer when active company changes ──────────────
  useEffect(() => {
    if (activeCompany?.id) {
      startAutoBackup(activeCompany.id);
    } else {
      stopAutoBackup();
    }
    return () => stopAutoBackup();
  }, [activeCompany?.id]);

  // Register global exit confirm — used by TopHeader close button & Quit
  useEffect(() => {
    (window as any).showExitConfirm = () => setShowExitModal(true);
    return () => { delete (window as any).showExitConfirm; };
  }, []);

  const handleExitConfirm = async () => {
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      getCurrentWindow().close();
    } catch {
      window.close();
    }
  };

  // ── Boot ──────────────────────────────────────────────────────────────────
  // resolveLicenseOnBoot:
  //   - reads IDB cache (fast)
  //   - if online, also checks Firestore installations/{installId}
  //   - returns best available license
  // This means: open Chrome, activate → open Firefox on same machine →
  // Firefox reads same installId from IDB → looks up Firestore → finds license
  // → no gate shown.
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (isMultiUserClient()) return;
      const resolved = await resolveLicenseOnBoot();

      if (cancelled) return;

      // If local/IDB license is already valid — skip network check entirely.
      // User already activated before; no need to block them for connectivity.
      if (!isLicenseValid(resolved)) {
        // No valid local license — check if we're online before showing gate
        // (first-time activation requires internet)
        const online = await checkOnline();
        if (!online) {
          setBootState('offline-blocked');
          return;
        }
      }

      setLicense(resolved);
      setBootState('ready');
    };

    boot();
    return () => { cancelled = true; };
  }, []);

  // Poll until online when offline-blocked
  useEffect(() => {
    if (bootState !== 'offline-blocked') return;
    const id = setInterval(async () => {
      const online = await checkOnline();
      if (online) {
        clearInterval(id);
        const resolved = await resolveLicenseOnBoot();
        setLicense(resolved);
        setBootState('ready');
      }
    }, 5000);
    return () => clearInterval(id);
  }, [bootState]);

  // Sync license to global store
  useEffect(() => { setLicenseInfo(license); }, [license, setLicenseInfo]);

  // DB init after valid license
  useEffect(() => {
    if (!isLicenseValid(license)) return;
    const initDb = async () => {
      if (isMultiUserClient()) {
        // Client mode: sync all data from server into local IndexedDB
        const result = await syncFromServer();
        console.log('Client sync:', result.message);
      } else {
        await seedDatabase();
      }
      const lastId = localStorage.getItem('lastActiveCompanyId');
      let active = null;
      if (lastId) active = await dexieDb.companies.get(Number(lastId));
      if (!active) active = await dexieDb.companies.where('isActive').equals(1).first()
                         || await dexieDb.companies.toCollection().first();
      if (active) setActiveCompany(active);
    };
    initDb();
  }, [license]);

  // Periodic local validity + expiry warning (every 5 min)
  useEffect(() => {
    if (isMultiUserClient()) return;
    const check = () => {
      const current = getStoredLicense();
      if (!current) return;
      if (current.expiresAt) {
        const days = (new Date(current.expiresAt).getTime() - Date.now()) / 86400000;
        if (days > 0 && days <= 3 && !expiryWarningShown.current) {
          expiryWarningShown.current = true;
          setExpiryWarning(`Your ${current.type === 'demo' ? 'trial' : 'license'} expiry in ${Math.ceil(days)} day(s).`);
        }
      }
      if (!isLicenseValid(current)) {
        clearStoredLicense();
        setLicense(null);
        if (current.expiresAt && new Date(current.expiresAt) < new Date()) {
          setExpirationMsg('Your license has expired. Please renew or contact support.');
        }
      }
    };
    check();
    const id = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Realtime revocation from Firestore
  useEffect(() => {
    if (isMultiUserClient()) return;
    if (unsubRevoke.current) { unsubRevoke.current(); unsubRevoke.current = null; }
    if (!license?.key) return;
    unsubRevoke.current = watchLicenseRevocation(license.key, () => {
      clearStoredLicense();
      setLicense(null);
      setExpirationMsg('Your license has been revoked. Please contact support.');
    });
    return () => { if (unsubRevoke.current) { unsubRevoke.current(); unsubRevoke.current = null; } };
  }, [license?.key]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleStartDemo = async () => {
    const info = await activateDemo(); // saves to IDB + Firestore
    setLicense(info);
    setExpirationMsg('');
  };

  const handleActivation = (key: string, plan: PlanType, expiresAt?: string | null, customer?: string) => {
    // By the time onActivate() is called, LicenseScreen has already confirmed
    // activateLicense() succeeded on the server (Firestore transaction committed
    // and set status:'active'). activateLicense() itself already persisted the
    // authoritative LicenseInfo via saveStoredLicense() — read that back instead
    // of reconstructing a fresh object here, so the UI never marks the app as
    // "licensed" based on data that wasn't actually confirmed by the server.
    const confirmed = getStoredLicense();
    if (confirmed && confirmed.key === key) {
      setLicense(confirmed);
      setExpirationMsg('');
      return;
    }
    // Fallback (should be rare): local cache missing for some reason — rebuild
    // from what we know, but this path should no longer be hit in practice.
    getInstallId().then(id => {
      const info: LicenseInfo = {
        type: 'full', plan,
        activatedAt: new Date().toISOString(),
        expiresAt: expiresAt ?? null,
        key, customer, deviceId: id,
      };
      saveStoredLicense(info);
      setLicense(info);
      setExpirationMsg('');
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (bootState === 'checking') {
    return (
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', background:'#0f1a1a', flexDirection:'column', gap:16,
      }}>
        <div style={{
          width:36, height:36, border:'3px solid #1e8585',
          borderTopColor:'transparent', borderRadius:'50%',
          animation:'spin 0.8s linear infinite',
        }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ color:'#64a0a0', fontSize:13 }}>Starting…</span>
      </div>
    );
  }

  if (bootState === 'offline-blocked') {
    return (
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', background:'#0f1a1a', flexDirection:'column', gap:20,
        padding:32, textAlign:'center',
      }}>
        <div style={{ fontSize:48 }}>📡</div>
        <div style={{ color:'#e2e8f0', fontSize:18, fontWeight:700 }}>
          Internet Connection Required
        </div>
        <div style={{ color:'#94a3b8', fontSize:13, maxWidth:360, lineHeight:1.6 }}>
          An internet connection is required to verify your license on first launch.
          The app will open automatically once a connection is detected.
        </div>
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          background:'#1e2d2d', padding:'10px 20px', borderRadius:8,
          border:'1px solid #1e8585',
        }}>
          <div style={{
            width:10, height:10, borderRadius:'50%', background:'#d97706',
            animation:'pulse 1.5s ease-in-out infinite',
          }}/>
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
          <span style={{ color:'#94a3b8', fontSize:12 }}>Waiting for connection…</span>
        </div>
      </div>
    );
  }

  if (!isLicenseValid(license)) {
    return (
      <LicenseScreen
        onStartDemo={handleStartDemo}
        onActivate={handleActivation}
        expirationMessage={expirationMsg}
      />
    );
  }

  return (
    <ErrorBoundary name="DashboardLayout">
      {showExitModal && (
        <ExitConfirmModal
          onConfirm={handleExitConfirm}
          onCancel={() => setShowExitModal(false)}
        />
      )}
      {trialLimitReason && (
        <TrialLimitModal
          reason={trialLimitReason}
          onUpgrade={() => { setTrialLimitReason(null); setShowLicensePanel(true); }}
          onDismiss={() => setTrialLimitReason(null)}
        />
      )}
      {expiryWarning && (
        <ExpiryWarningModal
          message={expiryWarning}
          onDismiss={() => setExpiryWarning(null)}
        />
      )}
      <DataProviderWrapper>
        <DashboardLayout />
      </DataProviderWrapper>
    </ErrorBoundary>
  );
}